import React, { useState, useMemo } from 'react';
import type { InventoryEntry, InventoryCountEntryLine } from './types';
import type { EnteriesListModel } from '@store/INV/InventoryCount/inventoryCountModels';
import { Card, CardBody, CardHeader } from '@components/card/card';
import Loading from '@components/Shared/Loader/Loading';
// EnteriesListModel
interface Props {
  entries: EnteriesListModel[];
  loading: boolean;
  error: any;
}

const InventoryEntriesList: React.FC<Props> = ({ entries, loading, error }) => {
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // استخراج قائمة المستخدمين الفريدة
  const uniqueUsers = useMemo(() => {
    const users = entries.map(entry => ({
      id: entry.personId,
      name: entry.personName,
      name2: entry.personName2
    }));

    // إزالة التكرارات
    return users.filter((user, index, self) => index === self.findIndex(u => u.id === user.id));
  }, [entries]);

  // إنشاء مصفوفة مسطحة من جميع البنود
  const allLines = useMemo(() => {
    const lines: Array<{
      entry: InventoryEntry;
      line: InventoryCountEntryLine;
      entryIndex: number;
    }> = [];

    entries.forEach((entry, entryIndex) => {
      entry.lines.forEach(line => {
        lines.push({
          entry,
          line,
          entryIndex
        });
      });
    });

    return lines;
  }, [entries]);

  // تصفية البيانات حسب المستخدم والبحث
  const filteredLines = useMemo(() => {
    let filtered = allLines;

    // التصفية حسب المستخدم
    if (selectedUser !== 'all') {
      filtered = filtered.filter(item => item.entry.personId === selectedUser);
    }

    // التصفية حسب البحث
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        item =>
          item.entry.personName.toLowerCase().includes(term) ||
          item.entry.personName2.toLowerCase().includes(term) ||
          item.line.description.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [allLines, selectedUser, searchTerm]);

  // إحصائيات عامة
  const totalStats = useMemo(() => {
    const totalEntries = [...new Set(filteredLines.map(item => item.entry.id))].length;
    const totalItems = filteredLines.length;
    const totalQuantity = filteredLines.reduce((sum, item) => sum + item.line.countQuantity, 0);

    return { totalEntries, totalItems, totalQuantity };
  }, [filteredLines]);

  if (loading && entries.length === 0) {
    return (
      <div className='text-center py-5'>
        <div className='spinner-border text-theme' role='status'>
          <span className='visually-hidden'>جاري التحميل...</span>
        </div>
        <p className='mt-2 text-muted'>جاري تحميل البيانات...</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className='text-center py-5'>
        <i className='fas fa-inbox fa-3x text-muted mb-3'></i>
        <p className='text-muted'>لا توجد إدخالات سابقة</p>
      </div>
    );
  }

  return (
    <>
      <Loading loading={loading} error={error}>
        <Card className='card mb-4'>
          <CardHeader className='d-flex justify-content-between align-items-center'>
            <h6 className='mb-0'>
              <i className='fas fa-clipboard-list me-2'></i>
              قائمة إدخالات الجرد
              <span className='badge bg-theme ms-2'>جميع الإدخالات</span>
            </h6>
            <div>
              <span className='badge bg-success me-2'>عدد البنود: {filteredLines.length}</span>
              <span className='badge bg-info'>الإجمالي: {totalStats.totalQuantity.toLocaleString()}</span>
            </div>
          </CardHeader>

          <CardBody className=' p-0'>
            <div>
              {/* أدوات البحث والفلترة */}
              <div className='p-3 border-bottom bg-secondary bg-opacity-45'>
                <div className='row g-2'>
                  <div className='col-md-8'>
                    <input
                      type='text'
                      className='form-control form-control-sm'
                      placeholder='ابحث باسم المستخدم أو وصف البند...'
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className='col-md-4'>
                    <select
                      className='form-select form-select-sm'
                      value={selectedUser}
                      onChange={e => setSelectedUser(e.target.value)}
                    >
                      <option value='all'>جميع المستخدمين</option>
                      {uniqueUsers.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.name2}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className='table-responsive'>
                <div
                  style={{
                    maxHeight: '550px',
                    overflowY: 'auto',
                    overflowX: 'auto',
                    position: 'relative'
                  }}
                >
                  <table className='table table-hover mb-0'>
                    <thead
                      className='table-active  fw-bold sticky-top shadow'
                      style={{
                        backdropFilter: 'blur(20px)'
                      }}
                    >
                      <tr>
                        <th className=' fw-bold'>#</th>
                        <th className=' fw-bold'>المستخدم</th>
                        <th className=' fw-bold'>التاريخ</th>
                        <th className=' fw-bold'>الوصف</th>
                        <th className=' fw-bold'>الكمية</th>
                        <th className=' fw-bold'>وحدة القياس</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLines.length === 0 ? (
                        <tr>
                          <td colSpan={8} className=' py-4'>
                            <i className='fas fa-search fa-2x text-muted mb-2'></i>
                            <p className='text-muted mb-0'>لا توجد نتائج مطابقة للبحث</p>
                            <button
                              className='btn btn-sm btn-outline-theme mt-2'
                              onClick={() => {
                                setSelectedUser('all');
                                setSearchTerm('');
                              }}
                            >
                              عرض جميع البنود
                            </button>
                          </td>
                        </tr>
                      ) : (
                        filteredLines.map((item, index) => {
                          const { entry, line } = item;

                          return (
                            <tr key={`${entry.id}-${line.id}`}>
                              <td className=' align-middle'>
                                <span className='text-muted small'>{index + 1}</span>
                              </td>
                              <td className=' align-middle'>
                                <div>
                                  <div className='fw-bold  small'>{entry.personName}</div>
                                  <small className='text-muted'>{entry.personName2}</small>
                                </div>
                              </td>
                              <td className=' align-middle'>
                                <div className='text-nowrap small'>{entry.entryDate.split('T')[0]}</div>
                              </td>
                              <td className=' align-middle'>
                                <div className='fw-bold  small'>{line.description}</div>
                              </td>
                              <td className=' align-middle'>
                                <span className='badge bg-success small'>{line.countQuantity.toLocaleString()}</span>
                              </td>
                              <td className=' align-middle'>
                                <span className='badge bg-secondary small'>
                                  {line.unitsOfMeasureName2 || line.unitsOfMeasureName}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </CardBody>

          <div className='card-footer d-flex justify-content-between align-items-center'>
            <div className='text-muted small'>
              عرض {filteredLines.length} من {allLines.length} بند
              {selectedUser !== 'all' && ` | مفلتر حسب: ${uniqueUsers.find(u => u.id === selectedUser)?.name2}`}
              {searchTerm && ` | البحث: "${searchTerm}"`}
            </div>
            <div className='text-theme fw-bold'>
              إجمالي الكميـات: {totalStats.totalQuantity.toLocaleString()}
              {totalStats.totalQuantity !== allLines.reduce((sum, item) => sum + item.line.countQuantity, 0) &&
                ` من ${allLines.reduce((sum, item) => sum + item.line.countQuantity, 0).toLocaleString()}`}
            </div>
          </div>
        </Card>
      </Loading>
    </>
  );
};

export default InventoryEntriesList;
